import { _ } from '/@/locale';
import {
    type CornCropMonitoring,
    CROP_MONITORING_KEYS,
    type CropMonitoring,
    type CropType,
    type ResultHybrid,
} from '../types/crop-monitoring';
import type { Field } from '../types/field';
import type { HistoricalWeather } from '../types/weather/forecast';

// Utility functions
export const getCropMonitoringKey = (crop: CropType): keyof CropMonitoring => {
    return CROP_MONITORING_KEYS[crop];
};

export const getCropMonitoringData = (cropMonitoring: CropMonitoring | undefined) => {
    if (!cropMonitoring) return;
    const key = getCropMonitoringKey(cropMonitoring.crop);
    return cropMonitoring[key] as CornCropMonitoring;
};

/**
 * True while an active corn CM may still be processed asynchronously on the server
 * (e.g. weather/GDU job after create or update). Used to drive short-lived SWR polling.
 */
/**
 * Display name for a hybrid. A generic hybrid is shown as "Híbrido Genérico" rather than the
 * resolved product name, unless it came from a FieldView planting: then the CM product holds
 * the raw FieldView seed name, which is kept. Otherwise the hybrid's own product name.
 */
export const getHybridProductName = (cropMonitoring: CropMonitoring | ResultHybrid | undefined): string | undefined => {
    const corn = cropMonitoring?.cornCropMonitoring;
    if (corn?.hasGenericProduct) {
        if (cropMonitoring?.sourcePlantingReport) return cropMonitoring.productName || _('Híbrido Genérico');
        return _('Híbrido Genérico');
    }
    return corn?.productName || cropMonitoring?.productName || undefined;
};

/** What tells seed names apart, as the backend's `seed_key`: case, spaces and punctuation don't. */
const seedKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The FieldView seed a hybrid was created from, when it reads differently from its product
 * name: several FieldView seeds (`DK72-72TRE`, `DK72-72RR2`) match the same catalog product
 * (`DK72-72`), so the seed tells those hybrids apart. Undefined for a hybrid made by hand.
 */
export const getHybridSeedName = (cropMonitoring: CropMonitoring | ResultHybrid | undefined): string | undefined => {
    const seed = cropMonitoring?.sourceSeedProductName?.trim();
    if (!seed) return undefined;
    const productName = getHybridProductName(cropMonitoring);
    return productName && seedKey(productName) === seedKey(seed) ? undefined : seed;
};

/**
 * Names of the hybrids a harvest covers. One harvest result is shared by the hybrids of the
 * field's campaign, so its own list names them. Before the harvest exists, the ones it will
 * be shared with (as the backend picks them: same crop and campaign, active without a result
 * of their own, planted before the harvest) come from `campaignHybrids`.
 */
export const getHarvestHybridNames = (
    cropMonitoring: CropMonitoring,
    { campaignHybrids = [], harvestDate }: { campaignHybrids?: CropMonitoring[]; harvestDate?: string } = {}
): string[] => {
    const resultHybrids = cropMonitoring.result?.hybrids;
    const hybrids: (CropMonitoring | ResultHybrid)[] = resultHybrids?.length
        ? resultHybrids
        : [
              cropMonitoring,
              ...(cropMonitoring.campaign == null
                  ? []
                  : campaignHybrids.filter(
                        (cm) =>
                            cm.id !== cropMonitoring.id &&
                            cm.active &&
                            !cm.result &&
                            cm.crop === cropMonitoring.crop &&
                            cm.campaign === cropMonitoring.campaign &&
                            (!harvestDate || cm.plantingDate < harvestDate)
                    )),
          ].sort((a, b) => a.id - b.id);
    return hybrids.map(getHybridProductName).filter((name): name is string => Boolean(name));
};

/** The hybrids sharing the harvest of `cropMonitoring`, when there are several; none otherwise. */
export const getSharedHarvestHybrids = (cropMonitoring: CropMonitoring | undefined): ResultHybrid[] => {
    const hybrids = cropMonitoring?.result?.hybrids ?? [];
    return hybrids.length > 1 ? hybrids : [];
};

/** The crop monitorings "Finalizar campaña" archives: the given one plus every active one of its field. */
export const getCampaignArchiveIds = (
    cropMonitoringId: number,
    field: Pick<Field, 'activeCropMonitorings'> | undefined
) => [...new Set([cropMonitoringId, ...(field?.activeCropMonitorings ?? []).map((cm) => cm.id)])];

export const cropMonitoringPendingAsyncState = (cropMonitoring: CropMonitoring | undefined): boolean => {
    if (!cropMonitoring?.active) return false;
    if (cropMonitoring.crop !== 'corn') return false;
    const corn = cropMonitoring.cornCropMonitoring;
    if (!corn) return true;
    if (corn.phenology === 'harvested') return false;
    return !corn.accumulatedGduDate;
};

/**
 * Whether a CM is worth re-fetching to pick up the async GDU/phenology job: still pending and
 * already planted (a future planting has nothing to compute yet, so polling it would never end).
 */
export const shouldPollPendingCropMonitoring = (
    cropMonitoring: CropMonitoring | undefined,
    now: number = Date.now()
): boolean =>
    cropMonitoringPendingAsyncState(cropMonitoring) &&
    cropMonitoring?.plantingDate != null &&
    new Date(cropMonitoring.plantingDate).getTime() <= now;

/**
 * True when the same CM went from pending to processed between two fetches. Switching to
 * another CM (different id, or no data yet while it loads) is not a settle.
 */
export const cropMonitoringSettled = (
    previous: { id?: number; pending: boolean } | undefined,
    current: { id?: number; pending: boolean }
): boolean => Boolean(previous?.pending && current.id != null && previous.id === current.id && !current.pending);

/**
 * Accumulated GDUs to display for a corn CM.
 *
 * The backend keeps accumulating `accumulatedGdu` daily until harvest, but GDUs stop
 * counting at physiological maturity (MF). Once an MF stage exists, freeze the number at
 * the accumulated value on the MF date, read from the same daily series the GDU graphic
 * plots (`gduAccum`) so card and graphic always agree.
 *
 * Falls back to the raw `accumulatedGdu` before MF, or when the MF day is missing from
 * the weather series (not loaded yet, or a gap in the data).
 */
export const getDisplayAccumulatedGdu = (
    cropMonitoring: CropMonitoring | undefined,
    historicalWeather: HistoricalWeather | undefined
): number | undefined => {
    const cropData = getCropMonitoringData(cropMonitoring);
    if (!cropData) return undefined;

    const maturityStage = cropData.stages?.find((stage) => stage.phenology === 'maturity');
    if (!maturityStage) return cropData.accumulatedGdu;

    // Compare the calendar day as a string: stage dates are date-only ("2026-02-15") while
    // weather dates carry a time part, so parsing both to `Date` would shift them onto
    // different local days and silently miss the match.
    const maturityDay = maturityStage.date.split('T')[0];
    const maturityWeather = historicalWeather?.results?.find((hw) => hw.date.split('T')[0] === maturityDay);
    if (maturityWeather?.gduAccum === undefined) return cropData.accumulatedGdu;

    return Math.round(maturityWeather.gduAccum);
};

export const isFallowPeriod = (plantingDate: string): boolean => {
    return new Date(plantingDate) > new Date();
};
